import { memo, useEffect, useRef, useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { useStore, NET_TERMINAL_HANDLE, type NetLabelNode as TNetLabelNode } from '../store';

/**
 * Net label — a named tag that joins its wired pin into a net. Net labels are
 * SHEET-LOCAL: same name merges only within the same sheet (KiCad local-label
 * semantics). For cross-sheet connection use a power symbol.
 */
function NetLabelNodeImpl({ id, data, selected }: NodeProps<TNetLabelNode>) {
  const setNetNodeName = useStore((s) => s.setNetNodeName);
  const [editing, setEditing] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const handlePosition = sideToPosition(data.side ?? 'left');

  useEffect(() => {
    if (editing) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [editing]);

  return (
    <div
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 22,
        height: 16,
        padding: '0 6px',
        background: 'var(--panel)',
        border: `1px solid ${selected ? 'var(--accent)' : '#64748b'}`,
        borderRadius: 4,
        cursor: editing ? 'text' : 'move',
        boxShadow: selected ? '0 0 0 2px var(--accent-soft)' : 'none',
      }}
    >
      <Handle type="source" id={NET_TERMINAL_HANDLE} position={handlePosition} isConnectableStart isConnectableEnd style={handleStyle} />
      <Handle type="target" id={NET_TERMINAL_HANDLE} position={handlePosition} isConnectableStart isConnectableEnd style={{ ...handleStyle, opacity: 0 }} />
      {editing ? (
        <input
          ref={ref}
          defaultValue={data.name}
          onBlur={(e) => {
            setNetNodeName(id, e.target.value.trim() || data.name);
            setEditing(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            else if (e.key === 'Escape') setEditing(false);
          }}
          style={nameInputStyle}
        />
      ) : (
        <span style={nameStyle}>{data.name}</span>
      )}
    </div>
  );
}

function sideToPosition(side: TNetLabelNode['data']['side']) {
  if (side === 'right') return Position.Right;
  if (side === 'top') return Position.Top;
  if (side === 'bottom') return Position.Bottom;
  return Position.Left;
}

const handleStyle = {
  width: 5,
  height: 5,
  background: '#fff',
  border: '1px solid #64748b',
  borderRadius: 999,
  left: -2,
};
const nameStyle: React.CSSProperties = {
  fontSize: 9.5,
  fontWeight: 800,
  letterSpacing: 0,
  color: 'var(--text)',
  fontFamily: 'ui-monospace, Consolas, monospace',
  whiteSpace: 'nowrap',
};
const nameInputStyle: React.CSSProperties = {
  width: 54,
  fontSize: 9.5,
  fontWeight: 800,
  border: 'none',
  background: 'transparent',
  outline: 'none',
  fontFamily: 'ui-monospace, Consolas, monospace',
  color: 'var(--text)',
};

export const NetLabelNode = memo(NetLabelNodeImpl);
